import { createFileRoute } from '@tanstack/react-router';
import { ContentHeader, ContentSection } from "@/components/content";

export const Route = createFileRoute('/legal/terms')({
  component: TermsOfServicePage,
  head: () => ({
    meta: [
      { title: 'Terms of Service | Matricks' },
      { name: 'description', content: 'Matricks Terms of Service. Read our terms and conditions for using the Matricks platform.' },
    ],
  }),
});

function TermsOfServicePage() {
  return (
    <div className="py-16 sm:py-24">
      <div className="container px-6">
        <ContentHeader
          title="Terms of Service"
          description="Last updated: December 2024"
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Legal", href: "/legal" },
            { label: "Terms of Service" },
          ]}
        />

        <div className="prose prose-slate dark:prose-invert max-w-none">
          {/* Introduction */}
          <ContentSection id="introduction" title="1. Introduction">
            <p className="text-muted-foreground text-lg">
              Welcome to Matricks. These Terms of Service (&quot;Terms&quot;) govern your access to and use
              of the Matricks platform, including our website, APIs, and related services
              (collectively, the &quot;Service&quot;). By accessing or using the Service, you agree to be
              bound by these Terms.
            </p>
            <p className="text-muted-foreground">
              Please read these Terms carefully before using our Service. If you do not agree to
              these Terms, you may not access or use the Service.
            </p>
          </ContentSection>

          {/* Definitions */}
          <ContentSection id="definitions" title="2. Definitions" background="muted">
            <div className="rounded-xl border bg-card p-6">
              <ul className="space-y-3 text-muted-foreground">
                <li>
                  <strong>&quot;Matricks,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;</strong> refers to Matricks, Inc.
                  and its affiliates.
                </li>
                <li>
                  <strong>&quot;You&quot; or &quot;your&quot;</strong> refers to the individual or entity accessing or
                  using the Service.
                </li>
                <li>
                  <strong>&quot;User&quot;</strong> means any individual who accesses or uses the Service.
                </li>
                <li>
                  <strong>&quot;Organization&quot;</strong> means a company, team, or other entity that
                  creates an account on the Service.
                </li>
                <li>
                  <strong>&quot;Content&quot;</strong> means any data, text, files, or other materials
                  uploaded, submitted, or transmitted through the Service.
                </li>
                <li>
                  <strong>&quot;Integration&quot;</strong> means a connection between the Service and a
                  third-party application or service.
                </li>
              </ul>
            </div>
          </ContentSection>

          {/* Account Terms */}
          <ContentSection id="account" title="3. Account Terms">
            <div className="space-y-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Account Creation</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>You must provide accurate and complete information when creating an account.</li>
                  <li>You must be at least 16 years old to use the Service.</li>
                  <li>If you are using the Service on behalf of an organization, you represent
                    that you have authority to bind that organization to these Terms.</li>
                  <li>You may not use another person&apos;s account without permission.</li>
                </ul>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Account Security</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>You are responsible for maintaining the security of your account and password.</li>
                  <li>You must notify us immediately of any unauthorized access or security breach.</li>
                  <li>You are responsible for all activities that occur under your account.</li>
                  <li>We recommend enabling multi-factor authentication for enhanced security.</li>
                </ul>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Account Termination</h3>
                <p className="text-muted-foreground">
                  You may terminate your account at any time through your account settings. We
                  reserve the right to suspend or terminate accounts that violate these Terms or
                  for any other reason at our discretion.
                </p>
              </div>
            </div>
          </ContentSection>

          {/* Acceptable Use */}
          <ContentSection id="acceptable-use" title="4. Acceptable Use" background="muted">
            <div className="space-y-6">
              <p className="text-muted-foreground">
                You agree to use the Service only for lawful purposes and in accordance with these
                Terms. You agree not to:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border bg-card p-6">
                  <h4 className="font-semibold mb-3">Prohibited Activities</h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>Violate any applicable laws or regulations</li>
                    <li>Infringe on intellectual property rights</li>
                    <li>Transmit malware, viruses, or harmful code</li>
                    <li>Attempt to gain unauthorized access to systems</li>
                    <li>Interfere with or disrupt the Service</li>
                    <li>Engage in fraudulent or deceptive practices</li>
                  </ul>
                </div>
                <div className="rounded-xl border bg-card p-6">
                  <h4 className="font-semibold mb-3">Prohibited Content</h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>Illegal or harmful content</li>
                    <li>Content that promotes violence or hatred</li>
                    <li>Sexually explicit material</li>
                    <li>Content that violates privacy rights</li>
                    <li>Spam or unsolicited communications</li>
                    <li>Misleading or false information</li>
                  </ul>
                </div>
              </div>
            </div>
          </ContentSection>

          {/* Service Terms */}
          <ContentSection id="service" title="5. Service Terms">
            <div className="space-y-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Service Availability</h3>
                <p className="text-muted-foreground">
                  We strive to provide reliable service but do not guarantee uninterrupted access.
                  We may modify, suspend, or discontinue any part of the Service at any time. We
                  will provide reasonable notice of material changes when possible.
                </p>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Integrations</h3>
                <p className="text-muted-foreground">
                  The Service allows you to connect to third-party applications and services. You
                  are responsible for complying with the terms of service of any third-party
                  services you connect. We are not responsible for the availability, reliability,
                  or security of third-party services.
                </p>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">API Usage</h3>
                <p className="text-muted-foreground">
                  If you use our APIs, you agree to comply with our API documentation and any
                  applicable rate limits. Excessive or abusive API usage may result in throttling
                  or suspension of access.
                </p>
              </div>
            </div>
          </ContentSection>

          {/* Payment Terms */}
          <ContentSection id="payment" title="6. Payment Terms" background="muted">
            <div className="space-y-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Fees and Billing</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>Fees are based on your selected subscription plan and usage.</li>
                  <li>All fees are quoted in USD unless otherwise specified.</li>
                  <li>Subscription fees are billed in advance on a monthly or annual basis.</li>
                  <li>Usage-based fees are billed in arrears.</li>
                  <li>You are responsible for providing accurate billing information.</li>
                </ul>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Taxes</h3>
                <p className="text-muted-foreground">
                  You are responsible for all applicable taxes. If we are required to collect
                  taxes, they will be added to your invoice. You agree to provide any information
                  necessary for tax compliance.
                </p>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Refunds</h3>
                <p className="text-muted-foreground">
                  We do not provide refunds for subscription fees except as required by law. If
                  you cancel your subscription, you will retain access until the end of your
                  current billing period.
                </p>
              </div>
            </div>
          </ContentSection>

          {/* Intellectual Property */}
          <ContentSection id="ip" title="7. Intellectual Property">
            <div className="space-y-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Our Intellectual Property</h3>
                <p className="text-muted-foreground">
                  The Service, including its design, features, and content created by us, is
                  protected by copyright, trademark, and other intellectual property laws. You may
                  not copy, modify, distribute, or create derivative works without our permission.
                </p>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Your Content</h3>
                <p className="text-muted-foreground">
                  You retain ownership of any Content you submit to the Service. By submitting
                  Content, you grant us a license to use, process, and store that Content as
                  necessary to provide the Service. This license terminates when you delete the
                  Content or close your account.
                </p>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Feedback</h3>
                <p className="text-muted-foreground">
                  If you provide us with feedback, suggestions, or ideas, you grant us the right
                  to use that feedback without restriction or compensation.
                </p>
              </div>
            </div>
          </ContentSection>

          {/* Warranties and Disclaimers */}
          <ContentSection id="warranties" title="8. Warranties and Disclaimers" background="muted">
            <div className="rounded-xl border bg-yellow-500/10 border-yellow-500/20 p-6">
              <p className="text-muted-foreground mb-4">
                THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY
                KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>Implied warranties of merchantability</li>
                <li>Fitness for a particular purpose</li>
                <li>Non-infringement</li>
                <li>Accuracy, reliability, or completeness of the Service</li>
                <li>Uninterrupted or error-free operation</li>
              </ul>
            </div>
          </ContentSection>

          {/* Limitation of Liability */}
          <ContentSection id="liability" title="9. Limitation of Liability">
            <div className="rounded-xl border bg-card p-6">
              <p className="text-muted-foreground mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>
                  We shall not be liable for any indirect, incidental, special, consequential,
                  or punitive damages, including loss of profits, data, or goodwill.
                </li>
                <li>
                  Our total liability for any claims arising from or related to these Terms or
                  the Service shall not exceed the amount you paid us in the twelve months
                  preceding the claim.
                </li>
                <li>
                  These limitations apply regardless of the theory of liability (contract, tort,
                  or otherwise).
                </li>
              </ul>
            </div>
          </ContentSection>

          {/* Governing Law */}
          <ContentSection id="governing-law" title="10. Governing Law and Disputes" background="muted">
            <div className="space-y-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Governing Law</h3>
                <p className="text-muted-foreground">
                  These Terms shall be governed by and construed in accordance with the laws of
                  the State of Delaware, without regard to its conflict of law provisions.
                </p>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Dispute Resolution</h3>
                <p className="text-muted-foreground">
                  Any disputes arising from these Terms or the Service shall first be addressed
                  through good-faith negotiation. If negotiation fails, disputes shall be resolved
                  through binding arbitration in accordance with the rules of the American
                  Arbitration Association.
                </p>
              </div>
            </div>
          </ContentSection>

          {/* Changes */}
          <ContentSection id="changes" title="11. Changes to Terms">
            <p className="text-muted-foreground">
              We may modify these Terms at any time. We will provide notice of material changes
              by posting the updated Terms on our website and updating the &quot;Last updated&quot; date.
              Your continued use of the Service after changes become effective constitutes your
              acceptance of the new Terms.
            </p>
          </ContentSection>

          {/* Contact */}
          <ContentSection id="contact" title="12. Contact Information" background="muted">
            <div className="rounded-xl border bg-card p-6">
              <p className="text-muted-foreground mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:legal@matricks.ai" className="text-primary hover:underline">
                    legal@matricks.ai
                  </a>
                </p>
                <p>
                  <strong>Address:</strong> Matricks, Inc., Legal Department
                </p>
              </div>
            </div>
          </ContentSection>
        </div>
      </div>
    </div>
  );
}
